import { createContext, useContext, createSignal, onCleanup, type JSX, type ParentProps } from "solid-js";
import type {
  WsIncoming,
  WsOutgoing,
  SessionState,
  AgentDependencyReport,
  AgentCredentialPublicKey,
  AccessSnapshot,
  AccessLevel,
  AccessState,
} from "./types";
import { wsOrigin, wsUrlList } from "./native";
import { apiFetch } from "./api-fetch";
import { refreshRuntimeConfig } from "./runtime-config";
import { isWsEnvelope } from "./ws-protocol";
import { recordBugReportWsMessage } from "./bug-report-capture";
import {
  decodeMuxFrame,
  decodeMuxJsonPayload,
  encodeMuxFrame,
  encodeMuxJsonFrame,
  isMuxBrowserEnabled,
  isMuxDataPlaneEnabled,
  isMuxFrame,
  MUX_DEFAULT_MAX_PAYLOAD_BYTES,
  muxBrowserReadyChannels,
  muxStableStreamId,
  muxStableStreamIdBytes,
  MuxChannel,
  MuxRoutingScope,
  rdpStreamIdFromFrame,
  rdpStreamIdFromTunnelId,
} from "./mux";
import { muxPluginNumericIdForTool } from "./mux-plugin-ids";

type Handler = (msg: WsIncoming) => void;
type BinaryHandler = (data: ArrayBuffer) => void;

export interface SessionPeer {
  id: string;
  role: string;
  label?: string;
  platform?: string;
  connectedAt?: number;
  active?: boolean;
  muxPeerIdx?: number;
  muxReady?: boolean;
  muxOperatorIdx?: number;
}

type WsStatus = "connecting" | "token_failed" | "open" | "closed" | "error";

interface WsApi {
  state: {
    sessionCode: string | null;
    agentFeatures: Record<string, boolean> | null;
    credentialKey: AgentCredentialPublicKey | null;
    agentDependencies: AgentDependencyReport | null;
    supportApproved: boolean;
    nativeAgentOnline: boolean;
    pluginVersions: Record<string, string>; // { choco: "1.0.0" }
    peers: SessionPeer[];
    wsStatus: WsStatus;
    lastError: string | null;
    myPeerId: string | null; // peerId du browser courant (depuis session:joined)
    muxReady: boolean;
    muxRelayRoute: boolean;
    muxAgentReady: boolean;
    muxPeerIndex: number | null;
    muxAgentPeerIndex: number | null;
    /** Lot 4 chantier politiques-host : exposé par publicState côté relai. */
    access: import("./types").AccessSnapshot | null;
    hostPolicy: import("./types").HostPolicy | null;
    /** Lot 4-bis : sessions RDS énumérées par le host. */
    rdsSessions: import("./types").RdsSession[] | null;
    /** Lot 8 : true si aucun user n'est connecté (écran de login Windows). */
    noUserLoggedIn: boolean;
  };
  send: (msg: WsOutgoing) => void;
  /** Envoie une trame WebSocket binaire brute (ex. tunnel RDP). */
  sendBinary: (data: ArrayBuffer) => void;
  on: (predicate: (msg: WsIncoming) => boolean, handler: Handler) => () => void;
  onBinary: (handler: BinaryHandler) => () => void;
  isOpen: () => boolean;
  bufferedAmount: () => number;
}

type AccessDecisionMessage = {
  type: "access:decision";
  level: AccessLevel;
  state: AccessState;
};

type SupportDecisionMessage = {
  type: "support:decision";
  approved: boolean;
};

type MuxHandshakeMessage = {
  type: "mux.handshake";
  version?: number;
  sessionId?: string;
  peerIndex?: number;
  peerIdx?: number;
  agentPeerIndex?: number;
  agentPeerIdx?: number;
  operatorId?: string | number;
  maxPayloadBytes?: number;
  relayRoute?: boolean;
  agentMuxReady?: boolean;
  channels?: string[];
  channelIds?: Record<string, number>;
  deliveryClasses?: Record<string, string>;
  flags?: string[];
};

type MuxAgentReadyMessage = {
  type: "mux.agent_ready";
  version?: number;
  agentPeerIndex?: number;
  agentPeerIdx?: number;
  relayRoute?: boolean;
};

const Ctx = createContext<WsApi>();

/// Égalité structurelle des payloads relais. Les objets comparés sortent du
/// même serializer (relais Rust) : ordre de clés stable → JSON.stringify est
/// une comparaison fiable et suffisante ici.
export function jsonEq(a: unknown, b: unknown): boolean {
  if (a === b) return true;
  return JSON.stringify(a) === JSON.stringify(b);
}

interface WsProviderProps extends ParentProps {
  /** Si fourni, ce provider ouvre une connexion dédiée et rejoint cette session. */
  sessionCode?: string | null;
  /** Ferme réellement la WS au démontage du provider (workspace host fermé). */
  closeOnCleanup?: boolean;
  /** Label envoyé au relay pour identifier ce peer opérateur. */
  joinLabel?: string;
}

export function WsProvider(props: WsProviderProps): JSX.Element {
  const [sessionCode, setSessionCode] = createSignal<string | null>(null);
  const [agentFeatures, setAgentFeatures] = createSignal<Record<string, boolean> | null>(null);
  const [credentialKey, setCredentialKey] = createSignal<AgentCredentialPublicKey | null>(null);
  const [agentDependencies, setAgentDependencies] = createSignal<AgentDependencyReport | null>(null);
  const [supportApproved, setSupportApproved] = createSignal(false);
  const [nativeAgentOnline, setNativeAgentOnline] = createSignal(false);
  // Lot 4 politiques-host : exposés via WsApi.state pour le panel Policy.
  const [access, setAccess] = createSignal<import("./types").AccessSnapshot | null>(null);
  const [hostPolicy, setHostPolicy] = createSignal<import("./types").HostPolicy | null>(null);
  // Lot 4-bis : sessions RDS énumérées par le host (WTSEnumerateSessionsEx).
  const [rdsSessions, setRdsSessions] = createSignal<import("./types").RdsSession[] | null>(null);
  // Lot 8 : true si aucun user n'est connecté (mire de login Windows affichée).
  const [noUserLoggedIn, setNoUserLoggedIn] = createSignal(false);
  const [pluginVersions, setPluginVersions] = createSignal<Record<string, string>>({});
  const [peers, setPeers] = createSignal<SessionPeer[]>([]);
  const [wsStatus, setWsStatus] = createSignal<WsStatus>("connecting");
  const [lastError, setLastError] = createSignal<string | null>(null);
  const [myPeerId, setMyPeerId] = createSignal<string | null>(null);
  const muxBrowserEnabled = isMuxBrowserEnabled();
  const muxDataPlaneEnabled = isMuxDataPlaneEnabled();
  const [muxReady, setMuxReady] = createSignal(false);
  const [muxRelayRoute, setMuxRelayRoute] = createSignal(false);
  const [muxAgentReady, setMuxAgentReady] = createSignal(false);
  const [muxPeerIndex, setMuxPeerIndex] = createSignal<number | null>(null);
  const [muxAgentPeerIndex, setMuxAgentPeerIndex] = createSignal<number | null>(null);
  const [muxMaxPayloadBytes, setMuxMaxPayloadBytes] = createSignal(MUX_DEFAULT_MAX_PAYLOAD_BYTES);
  const handlers = new Set<{ predicate: (m: WsIncoming) => boolean; handler: Handler }>();
  const binaryHandlers = new Set<BinaryHandler>();

  // Lot C — Liste de relais de fallback. Le primaire est `urls[0]`, après
  // 3 échecs consécutifs on rote sur le suivant (active-passive). Si la liste
  // contient 1 seul URL, comportement identique au legacy (single-relay).
  let urls = wsUrlList();
  let currentIdx = 0;
  let failuresOnCurrent = 0;
  const currentWsUrl = () => urls[currentIdx % urls.length];
  void wsOrigin; // évite warning ESLint si plus utilisé directement

  let ws: WebSocket | null = null;
  let backoff = 1000;
  let reconnectDisabled = false;

  const autoJoinSession = new URLSearchParams(location.search).get("s");
  const joinSession = () => props.sessionCode || autoJoinSession;
  const kickedKey = autoJoinSession ? `rws.session.kicked.${autoJoinSession}` : null;

  function isKickSuppressed(): boolean {
    if (!kickedKey) return false;
    try {
      const ts = Number(sessionStorage.getItem(kickedKey) || "0");
      return Number.isFinite(ts) && Date.now() - ts < 60 * 60 * 1000;
    } catch {
      return false;
    }
  }

  function suppressReconnect(reason: string): void {
    reconnectDisabled = true;
    setWsStatus("closed");
    setLastError(reason);
    setPeers([]);
    setMyPeerId(null);
    setMuxReady(false);
    setMuxRelayRoute(false);
    setMuxAgentReady(false);
    setMuxPeerIndex(null);
    setMuxAgentPeerIndex(null);
    try {
      if (kickedKey) sessionStorage.setItem(kickedKey, String(Date.now()));
    } catch {
      /* noop */
    }
  }

  function dispatchBinaryFrame(data: ArrayBuffer): void {
    for (const h of binaryHandlers) {
      try {
        h(data);
      } catch (err) {
        console.error("[ws binary]", err);
      }
    }
  }

  function isMuxHandshakeMessage(msg: unknown): msg is MuxHandshakeMessage {
    return Boolean(msg && typeof msg === "object" && (msg as { type?: unknown }).type === "mux.handshake");
  }

  function readU16(value: unknown): number | null {
    return typeof value === "number" && Number.isInteger(value) && value >= 0 && value <= 0xffff ? value : null;
  }

  function readMaxPayloadBytes(value: unknown): number {
    return typeof value === "number" && Number.isInteger(value) && value > 0 && value <= MUX_DEFAULT_MAX_PAYLOAD_BYTES
      ? value
      : MUX_DEFAULT_MAX_PAYLOAD_BYTES;
  }

  function negotiatedMuxReadyChannels(handshake: MuxHandshakeMessage): string[] {
    const localChannels = muxBrowserReadyChannels();
    if (!Array.isArray(handshake.channels)) return localChannels;
    const relayChannels = new Set(
      handshake.channels.filter((channel): channel is string => typeof channel === "string"),
    );
    return localChannels.filter((channel) => relayChannels.has(channel));
  }

  function sendMuxReady(handshake: MuxHandshakeMessage, wsUrl: string, currentWs: WebSocket): void {
    if (currentWs.readyState !== WebSocket.OPEN) {
      console.warn(`[ws mux] sendMuxReady abandonné — ws.readyState=${currentWs.readyState}`);
      return;
    }
    const raw = JSON.stringify({
      type: "mux.ready",
      version: 1,
      peerIndex: muxPeerIndex(),
      agentPeerIndex: muxAgentPeerIndex(),
      maxPayloadBytes: muxMaxPayloadBytes(),
      channels: negotiatedMuxReadyChannels(handshake),
      flags: ["json_payload_v1"],
    });
    recordBugReportWsMessage("out", raw, wsUrl);
    currentWs.send(raw);
  }

  function handleMuxHandshake(msg: MuxHandshakeMessage, wsUrl: string, currentWs: WebSocket): boolean {
    if (!muxBrowserEnabled || msg.version !== 1) {
      console.warn(
        `[ws mux] handleMuxHandshake rejeté — muxBrowserEnabled=${muxBrowserEnabled}, version=${msg.version}`,
      );
      return false;
    }
    setMuxPeerIndex(readU16(msg.peerIndex ?? msg.peerIdx));
    setMuxAgentPeerIndex(readU16(msg.agentPeerIndex ?? msg.agentPeerIdx));
    setMuxMaxPayloadBytes(readMaxPayloadBytes(msg.maxPayloadBytes));
    setMuxRelayRoute(msg.relayRoute === true);
    setMuxAgentReady(msg.agentMuxReady === true);
    setMuxReady(true);
    sendMuxReady(msg, wsUrl, currentWs);
    return true;
  }

  function isMuxAgentReadyMessage(msg: unknown): msg is MuxAgentReadyMessage {
    return Boolean(msg && typeof msg === "object" && (msg as { type?: unknown }).type === "mux.agent_ready");
  }

  function handleMuxAgentReady(msg: MuxAgentReadyMessage): boolean {
    if (!muxBrowserEnabled || msg.version !== 1) return false;
    const agentPeerIndex = readU16(msg.agentPeerIndex ?? msg.agentPeerIdx);
    if (agentPeerIndex !== null) setMuxAgentPeerIndex(agentPeerIndex);
    if (msg.relayRoute === true) setMuxRelayRoute(true);
    setMuxAgentReady(true);
    return true;
  }

  function handleMuxBinary(data: ArrayBuffer, wsUrl: string, currentWs: WebSocket): void {
    try {
      const frame = decodeMuxFrame(data, { maxPayloadBytes: muxMaxPayloadBytes() });
      switch (frame.header.channel) {
        case MuxChannel.Control:
        case MuxChannel.PluginData:
        case MuxChannel.PluginControl:
        case MuxChannel.Chat:
        case MuxChannel.Metrics: {
          const msg = decodeMuxJsonPayload<WsIncoming>(frame.payload);
          recordBugReportWsMessage("in", JSON.stringify(msg), wsUrl);
          handleIncomingMessage(msg, wsUrl, currentWs);
          return;
        }
        case MuxChannel.Video:
        case MuxChannel.Rdp:
        case MuxChannel.File:
          dispatchBinaryFrame(frame.payload);
          return;
        default:
          return;
      }
    } catch (err) {
      console.warn(`[ws mux] frame ignorée : ${err instanceof Error ? err.message : String(err)}`);
    }
  }

  function canSendMuxToAgent(): boolean {
    return (
      muxBrowserEnabled &&
      muxDataPlaneEnabled &&
      muxReady() &&
      muxRelayRoute() &&
      muxAgentReady() &&
      muxAgentPeerIndex() !== null &&
      ws?.readyState === WebSocket.OPEN
    );
  }

  function muxChannelForBinary(bytes: Uint8Array): MuxChannel | null {
    if (bytes.byteLength >= 18 && bytes[0] === 0x52 && bytes[1] === 0x44) return MuxChannel.Rdp;
    if (bytes.byteLength >= 18 && bytes[0] === 0x46 && bytes[1] === 0x42) return MuxChannel.File;
    return null;
  }

  function muxChannelForJson(msg: WsOutgoing): MuxChannel | null {
    const type = (msg as { type?: unknown }).type;
    const envelope = msg as { family?: unknown; op?: unknown };
    if (envelope.family === "control" && type === "tool" && envelope.op === "request") {
      return MuxChannel.PluginControl;
    }
    if (typeof type !== "string") return null;
    if (type === "input") return MuxChannel.Input;
    if (
      type === "video:control" ||
      type === "video:viewport" ||
      type === "video:viewrect" ||
      type === "video:metrics" ||
      type === "rdp:open" ||
      type === "rdp:close" ||
      type === "sas:request" ||
      type === "agent:credential_key_request" ||
      type === "support:request" ||
      type === "operator:capabilities" ||
      type === "video:mode_request" ||
      type.startsWith("webrtc:") ||
      type.startsWith("rtc:") ||
      type.startsWith("clipboard:") ||
      type.startsWith("e2e:") ||
      type.startsWith("access:") ||
      type.startsWith("rds:") ||
      type.startsWith("unattended:") ||
      type.startsWith("videohost:") ||
      type.startsWith("display:") ||
      type.startsWith("virtual_display:") ||
      type.startsWith("file:")
    ) {
      return MuxChannel.Control;
    }
    return null;
  }

  function streamIdForBinary(channel: MuxChannel, bytes: Uint8Array): number {
    if (channel === MuxChannel.Rdp && bytes.byteLength >= 18) {
      return rdpStreamIdFromFrame(bytes);
    }
    if (channel === MuxChannel.File && bytes.byteLength >= 18) {
      const idLen = (bytes[4] << 8) | bytes[5];
      const idStart = 18;
      if (idLen > 0 && bytes.byteLength >= idStart + idLen) {
        return muxStableStreamIdBytes(bytes.subarray(idStart, idStart + idLen));
      }
    }
    return 1;
  }

  function streamIdForJson(channel: MuxChannel, msg: WsOutgoing): number {
    if (channel === MuxChannel.Input) return 1;
    if (channel === MuxChannel.PluginControl) {
      const envelope = msg as { id?: unknown; payload?: { requestId?: unknown } };
      const requestId = envelope.payload?.requestId ?? envelope.id;
      return typeof requestId === "string" ? muxStableStreamId(requestId) : 1;
    }
    const type = (msg as { type?: unknown }).type;
    const tunnelId = (msg as { tunnelId?: unknown }).tunnelId;
    if ((type === "rdp:open" || type === "rdp:close") && typeof tunnelId === "string") {
      return rdpStreamIdFromTunnelId(tunnelId) ?? muxStableStreamId(tunnelId);
    }
    if (channel === MuxChannel.Control) {
      const control = msg as {
        requestId?: unknown;
        shadowId?: unknown;
        operatorId?: unknown;
        level?: unknown;
        sessionId?: unknown;
        fileId?: unknown;
        topic?: unknown;
        payload?: { topic?: unknown };
      };
      const value =
        control.requestId ??
        control.shadowId ??
        control.operatorId ??
        control.level ??
        control.sessionId ??
        control.fileId ??
        control.payload?.topic ??
        control.topic ??
        type;
      return typeof value === "string" ? muxStableStreamId(value) : 1;
    }
    return 0;
  }

  function pluginIdForJson(channel: MuxChannel, msg: WsOutgoing): number | undefined {
    if (channel !== MuxChannel.PluginControl) return undefined;
    const tool = (msg as { payload?: { tool?: unknown } }).payload?.tool;
    return typeof tool === "string" ? muxPluginNumericIdForTool(tool) : undefined;
  }

  function trySendMuxBinary(data: ArrayBuffer): boolean {
    if (isMuxFrame(data)) return false;
    const bytes = new Uint8Array(data);
    const channel = muxChannelForBinary(bytes);
    if (channel === null) return false;
    if (!canSendMuxToAgent()) {
      console.error(`[ws mux] envoi ${MuxChannel[channel]} bloqué : MUX non prêt.`);
      return true;
    }
    try {
      ws!.send(
        encodeMuxFrame(
          {
            channel,
            toPeerIdx: muxAgentPeerIndex()!,
            streamId: streamIdForBinary(channel, bytes),
            routingScope: MuxRoutingScope.UnicastPeer,
          },
          bytes,
          { maxPayloadBytes: muxMaxPayloadBytes() },
        ),
      );
      return true;
    } catch (err) {
      console.error(`[ws mux] envoi binaire bloqué : ${err instanceof Error ? err.message : String(err)}`);
      return true;
    }
  }

  function trySendMuxJson(msg: WsOutgoing): boolean {
    const channel = muxChannelForJson(msg);
    if (channel === null) return false;
    if (!canSendMuxToAgent()) {
      const type = (msg as { type?: unknown }).type;
      console.error(`[ws mux] envoi JSON ${typeof type === "string" ? type : "unknown"} bloqué : MUX non prêt.`);
      return true;
    }
    try {
      ws!.send(
        encodeMuxJsonFrame(
          {
            channel,
            toPeerIdx: muxAgentPeerIndex()!,
            streamId: streamIdForJson(channel, msg),
            pluginId: pluginIdForJson(channel, msg),
            routingScope: MuxRoutingScope.UnicastPeer,
          },
          msg,
          { maxPayloadBytes: muxMaxPayloadBytes() },
        ),
      );
      return true;
    } catch (err) {
      console.error(`[ws mux] envoi JSON bloqué : ${err instanceof Error ? err.message : String(err)}`);
      return true;
    }
  }

  function handleIncomingMessage(msg: WsIncoming | MuxHandshakeMessage, wsUrl: string, currentWs: WebSocket): void {
    if (isMuxHandshakeMessage(msg) && handleMuxHandshake(msg, wsUrl, currentWs)) return;
    if (isMuxAgentReadyMessage(msg) && handleMuxAgentReady(msg)) return;
    const incoming = msg as WsIncoming;
    if (incoming.type === "error" && (incoming as { code?: string }).code === "disconnected_by_operator") {
      suppressReconnect((incoming as { message?: string }).message || "Session opérateur fermée par un opérateur.");
      try {
        ws?.close(1000, "disconnected_by_operator");
      } catch {
        /* noop */
      }
      return;
    }
    if (incoming.type === "session:joined") {
      const m = incoming as { type: "session:joined"; peerId?: string };
      if (m.peerId) setMyPeerId(m.peerId);
    }
    if (incoming.type === "session:state") {
      const s = incoming as SessionState;
      const previousSessionCode = sessionCode();
      if (previousSessionCode && previousSessionCode !== s.sessionCode) setCredentialKey(null);
      setSessionCode(s.sessionCode);
      // Discipline de propagation : les `session:state` arrivent en RAFALE
      // (multi-op : chaque operator:activate déclenche un broadcast_state) en
      // re-livrant des objets NEUFS à contenu souvent identique. Re-setter
      // aveuglément ces signals propage une nouvelle référence à tous les
      // consommateurs (memos, <For>…) et remonte des panels qui n'ont pas
      // changé — perte d'état UI, teardown du client RDP (tunnel coupé +
      // mot de passe vidé). On ne notifie que sur changement STRUCTUREL réel
      // (les payloads sortent du même serializer relais : la comparaison
      // JSON est stable). S'applique à TOUTES les valeurs objet du state.
      let stateChanged = false;
      setAgentFeatures((prev) => {
        const next = s.agentFeatures || null;
        if (jsonEq(prev, next)) return prev;
        stateChanged = true;
        return next;
      });
      setAgentDependencies((prev) => {
        const next = s.agentDependencies || null;
        if (jsonEq(prev, next)) return prev;
        stateChanged = true;
        return next;
      });
      const nextAccess = s.access || null;
      const nextSupportApproved =
        typeof s.supportApproved === "boolean"
          ? s.supportApproved
          : !!(nextAccess && ["approved", "not_required"].includes(nextAccess.view));
      setSupportApproved((prev) => {
        if (prev === nextSupportApproved) return prev;
        stateChanged = true;
        return nextSupportApproved;
      });
      setNativeAgentOnline((prev) => {
        if (prev === s.nativeAgentOnline) return prev;
        stateChanged = true;
        return s.nativeAgentOnline;
      });
      if (!s.nativeAgentOnline) setCredentialKey(null);
      setAccess((prev) => {
        if (jsonEq(prev, nextAccess)) return prev;
        stateChanged = true;
        return nextAccess;
      });
      setHostPolicy((prev) => {
        const next = s.hostPolicy ?? null;
        if (jsonEq(prev, next)) return prev;
        stateChanged = true;
        return next;
      });
      setRdsSessions((prev) => {
        const next = s.rdsSessions ?? null;
        if (jsonEq(prev, next)) return prev;
        stateChanged = true;
        return next;
      });
      setNoUserLoggedIn((prev) => {
        const next = Boolean(s.noUserLoggedIn);
        if (prev === next) return prev;
        stateChanged = true;
        return next;
      });
      const sp = (s as unknown as { peers?: SessionPeer[] }).peers;
      const nextPeers = Array.isArray(sp) ? sp : [];
      setPeers((prev) => {
        if (jsonEq(prev, nextPeers)) return prev;
        stateChanged = true;
        return nextPeers;
      });
      void stateChanged;
    }
    // access:decision met à jour le sous-état d'access sans attendre le
    // prochain session:state. Permet à l'UI de réagir en < 100 ms.
    if (incoming.type === "access:decision") {
      const ad = incoming as AccessDecisionMessage;
      const cur = access();
      const next = {
        view: "pending",
        control: "pending",
        privacyScreen: "off",
        localInputBlock: "off",
        ...(cur || {}),
        [ad.level]: ad.state,
      } as AccessSnapshot;
      setAccess(next);
      setSupportApproved(next.view === "approved" || next.view === "not_required");
    }
    if (incoming.type === "support:decision") {
      const sd = incoming as SupportDecisionMessage;
      setSupportApproved(sd.approved);
    }
    if (incoming.type === "session:features") {
      setAgentFeatures((incoming as { type: "session:features"; features: Record<string, boolean> }).features);
    }
    if (incoming.type === "agent:credential_key") {
      setCredentialKey(incoming as AgentCredentialPublicKey);
    }
    if (incoming.type === "session:dependencies") {
      setAgentDependencies((incoming as { type: "session:dependencies"; report: AgentDependencyReport }).report);
    }
    if (isWsEnvelope(incoming, "live", "plugin", "capabilities")) {
      const caps = ((incoming as { payload?: { capabilities?: { versions?: Record<string, string> } } }).payload
        ?.capabilities ?? {}) as { versions?: Record<string, string> };
      if (caps?.versions) {
        setPluginVersions((prev) => {
          if (jsonEq(prev, caps.versions)) return prev;
          return caps.versions!;
        });
      }
    }
    for (const h of handlers) {
      try {
        if (h.predicate(incoming)) h.handler(incoming);
      } catch (err) {
        console.error("[ws handler]", err);
      }
    }
  }

  // Le relay exige un JWT (le cookie BetterAuth ne suffit pas pour le WS).
  // On récupère un JWT court via le backend, puis on le transmet au relay
  // dans le sous-protocole `jwt.<token>` (header WS impossible côté navigateur).
  async function fetchWsToken(): Promise<string | null> {
    try {
      await refreshRuntimeConfig();
      urls = wsUrlList();
      const res = await apiFetch("/api/ws-token", { credentials: "include" });
      if (!res.ok) return null;
      const data = (await res.json()) as { token?: string };
      return data.token || null;
    } catch {
      return null;
    }
  }

  async function connect(): Promise<void> {
    if (reconnectDisabled || isKickSuppressed()) {
      suppressReconnect("Session opérateur fermée par un opérateur.");
      return;
    }
    // Évite d'ouvrir plusieurs WebSockets si le provider est re-monté rapidement
    // ou si une connexion est déjà active. La logique de reconnexion reprendra
    // via le handler close quand l'existante tombera.
    if (ws?.readyState === WebSocket.OPEN || ws?.readyState === WebSocket.CONNECTING) {
      console.debug(`[ws] connect() ignoré — ws déjà ${ws!.readyState === WebSocket.OPEN ? "ouvert" : "en cours"}`);
      return;
    }
    setWsStatus("connecting");
    const token = await fetchWsToken();
    if (reconnectDisabled) return;
    if (!token) {
      setWsStatus("token_failed");
      setLastError("Impossible d'obtenir un token WS — session expirée ?");
      // Pas de session valide — on retente plus tard (l'opérateur doit se logger).
      setTimeout(connect, backoff);
      backoff = Math.min(backoff * 2, 15000);
      return;
    }
    const wsUrl = currentWsUrl();
    if (urls.length > 1) {
      console.info(`[ws] connect ${wsUrl} (${currentIdx + 1}/${urls.length}, failures=${failuresOnCurrent})`);
    }
    ws = new WebSocket(wsUrl, [`jwt.${token}`]);
    ws.binaryType = "arraybuffer";
    ws.addEventListener("open", () => {
      backoff = 1000;
      failuresOnCurrent = 0; // reset after a successful connect
      setWsStatus("open");
      setLastError(null);
      setMuxReady(false);
      setMuxRelayRoute(false);
      setMuxAgentReady(false);
      setMuxPeerIndex(null);
      setMuxAgentPeerIndex(null);
      setMuxMaxPayloadBytes(MUX_DEFAULT_MAX_PAYLOAD_BYTES);
      const targetSession = joinSession();
      if (targetSession) {
        ws!.send(
          JSON.stringify({
            type: "session:join",
            role: "operator",
            sessionCode: targetSession,
            ...(props.joinLabel ? { label: props.joinLabel } : {}),
          }),
        );
      }
    });
    ws.addEventListener("error", (ev) => {
      setWsStatus("error");
      setLastError(`WS error: ${(ev as Event).type || "unknown"}`);
    });
    const currentWs = ws;
    ws.addEventListener("message", (evt) => {
      if (evt.data instanceof ArrayBuffer) {
        if (muxBrowserEnabled && isMuxFrame(evt.data)) handleMuxBinary(evt.data, wsUrl, currentWs);
        else dispatchBinaryFrame(evt.data);
        return;
      }
      let msg: WsIncoming | MuxHandshakeMessage;
      try {
        const raw = typeof evt.data === "string" ? evt.data : new TextDecoder().decode(evt.data as ArrayBuffer);
        recordBugReportWsMessage("in", raw, wsUrl);
        msg = JSON.parse(raw) as WsIncoming | MuxHandshakeMessage;
      } catch {
        return;
      }
      handleIncomingMessage(msg, wsUrl, currentWs);
    });
    ws.addEventListener("close", (ev) => {
      setWsStatus("closed");
      setMuxReady(false);
      setMuxRelayRoute(false);
      setMuxAgentReady(false);
      setMuxPeerIndex(null);
      setMuxAgentPeerIndex(null);
      if (reconnectDisabled || isKickSuppressed()) {
        return;
      }
      if (ev.code !== 1000 && ev.code !== 1001) {
        setLastError(`WS fermé (code ${ev.code}${ev.reason ? `: ${ev.reason}` : ""})`);
        // Comptabilise comme un échec sur le relai courant. Après 3 échecs
        // consécutifs sur le même URL, on rote sur le suivant (active-passive).
        failuresOnCurrent++;
        if (failuresOnCurrent >= 3 && urls.length > 1) {
          currentIdx = (currentIdx + 1) % urls.length;
          failuresOnCurrent = 0;
          backoff = 1000; // reset backoff au moment de changer de cible

          console.warn(`[ws] rotation → ${currentWsUrl()}`);
        }
      }
      setTimeout(() => void connect(), backoff);
      backoff = Math.min(backoff * 2, 15000);
    });
  }
  void connect();

  // ── Durée de vie du WS = durée de vie de l'onglet, pas du composant ────────
  // Le `WsProvider` est monté par une route SolidJS (`console-session.tsx`,
  // `console.tsx`). Le routeur démonte ce composant à CHAQUE navigation SPA
  // (changer de section/onglet console, revenir à la liste hôtes, ouvrir le
  // super-admin…). Fermer le WS dans `onCleanup` revenait donc à fermer la
  // session opérateur sur un simple changement d'onglet — bug.
  //
  // Sémantique voulue : la session WS ne doit se fermer QUE sur une vraie
  // disparition de l'onglet (fermeture/rechargement = `pagehide`). Un
  // démontage SPA ordinaire NE ferme PAS le WS ; la reconnexion/rotation
  // existante reste intacte. Le logout (`window.location.href=…`) et la
  // fermeture d'onglet déclenchent `pagehide` → close propre.
  let documentUnloading = false;
  const onPageHide = (ev: PageTransitionEvent) => {
    // `persisted` = bfcache : l'onglet peut revenir, on ne tue pas le WS.
    if (ev.persisted) return;
    documentUnloading = true;
    reconnectDisabled = true; // empêche toute reconnexion pendant l'unload
    try {
      ws?.close(1000, "tab_closed");
    } catch {
      /* noop */
    }
  };
  if (typeof window !== "undefined") {
    window.addEventListener("pagehide", onPageHide);
  }

  onCleanup(() => {
    handlers.clear();
    if (typeof window !== "undefined") {
      window.removeEventListener("pagehide", onPageHide);
    }
    // Démontage SPA (navigation) : on PRÉSERVE le WS pour que la session
    // survive aux changements d'onglet/section. On ne ferme que si le
    // document est réellement en train de disparaître (pagehide a posé le
    // flag avant que SolidJS ne déclenche le cleanup).
    if (props.closeOnCleanup) {
      reconnectDisabled = true;
      try {
        ws?.close(1000, "provider_unmount");
      } catch {
        /* noop */
      }
    } else if (documentUnloading) {
      ws?.close();
    }
  });

  const api: WsApi = {
    get state() {
      return {
        sessionCode: sessionCode(),
        agentFeatures: agentFeatures(),
        credentialKey: credentialKey(),
        agentDependencies: agentDependencies(),
        supportApproved: supportApproved(),
        nativeAgentOnline: nativeAgentOnline(),
        pluginVersions: pluginVersions(),
        peers: peers(),
        wsStatus: wsStatus(),
        lastError: lastError(),
        myPeerId: myPeerId(),
        muxReady: muxReady(),
        muxRelayRoute: muxRelayRoute(),
        muxAgentReady: muxAgentReady(),
        muxPeerIndex: muxPeerIndex(),
        muxAgentPeerIndex: muxAgentPeerIndex(),
        access: access(),
        hostPolicy: hostPolicy(),
        rdsSessions: rdsSessions(),
        noUserLoggedIn: noUserLoggedIn(),
      };
    },
    send: (msg) => {
      if (ws && ws.readyState === WebSocket.OPEN) {
        if (trySendMuxJson(msg)) return;
        const raw = JSON.stringify(msg);
        recordBugReportWsMessage("out", raw, ws.url);
        ws.send(raw);
      }
    },
    sendBinary: (data) => {
      // Trame WebSocket binaire brute (le navigateur émet un frame binaire
      // quand on passe un ArrayBuffer/TypedArray à ws.send).
      // Compare à 1 (=OPEN) en LITTÉRAL : si la prototype globale a été
      // substituée (cf. rdp-transport.ts), WebSocket.OPEN peut ne plus être
      // résolu — la valeur littérale 1 est l'invariant WebSocket spec.
      const rs = ws?.readyState;
      if (ws && rs === 1) {
        try {
          if (trySendMuxBinary(data)) return;
          ws.send(data);
        } catch (e) {
          console.error(`[ws] sendBinary échec : ${e instanceof Error ? e.message : String(e)}`);
        }
      } else {
        console.warn(`[ws] sendBinary ignoré — ws=${!!ws} readyState=${rs}`);
      }
    },
    on: (predicate, handler) => {
      const entry = { predicate, handler };
      handlers.add(entry);
      return () => {
        handlers.delete(entry);
      };
    },
    onBinary: (handler) => {
      binaryHandlers.add(handler);
      return () => {
        binaryHandlers.delete(handler);
      };
    },
    isOpen: () => ws?.readyState === WebSocket.OPEN,
    bufferedAmount: () => ws?.bufferedAmount ?? 0,
  };

  return <Ctx.Provider value={api}>{props.children}</Ctx.Provider>;
}

export function useWs(): WsApi {
  const ctx = useContext(Ctx);
  if (!ctx) throw new Error("useWs() hors WsProvider");
  return ctx;
}
